import React from "react"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/shared/components/ui/card"
import { Button } from "@/shared/components/ui/button"
import { Cog, BellRing, ShieldCheck, Server, FileText, Palette, Database, Bell, Shield } from "lucide-react"
import { useNavigate } from "react-router-dom"

const Settings: React.FC = () => {
  const navigate = useNavigate()

  const settingsOptions = [
    {
      icon: Server,
      title: "Company Information",
      description: "Manage company details, contact information, and social media",
      link: "/admin/company-info",
      color: "text-blue-500"
    },
    {
      icon: FileText,
      title: "Legal Documents",
      description: "Upload and manage Privacy Policy and Terms of Service PDFs",
      link: "/admin/legal-documents",
      color: "text-red-500"
    },
    {
      icon: BellRing,
      title: "Notifications",
      description: "View and manage your notifications",
      link: "/admin/notifications",
      color: "text-green-500"
    },
    {
      icon: ShieldCheck,
      title: "Account Settings",
      description: "Update your profile and change your password",
      link: "/admin/account",
      color: "text-black"
    },
    {
      icon: Palette,
      title: "Appearance",
      description: "Customize the look and feel of your admin panel (Coming soon)",
      link: "#",
      color: "text-purple-500",
      disabled: true
    }
  ]

  return (
    <div className="space-y-6">
      <div className="p-6 bg-gradient-to-r from-slate-50 to-blue-50 rounded-xl border border-slate-200/50 shadow-sm">
        <h1 className="text-3xl font-bold flex items-center gap-3 bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent">
          <Cog className="h-8 w-8 text-blue-500" />
          Settings
        </h1>
        <p className="text-slate-600 mt-2 font-medium">Manage your preferences and system settings</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {settingsOptions.map((option, index) => {
          const Icon = option.icon
          return (
            <Card 
              key={index}
              className={`bg-gradient-to-br from-white to-slate-50 border-slate-200/60 shadow-sm hover:shadow-md transition-all duration-300 backdrop-blur-sm ${
                option.disabled 
                  ? 'opacity-60 cursor-not-allowed' 
                  : 'cursor-pointer hover:border-blue-300/50 hover:shadow-lg'
              }`}
              onClick={() => !option.disabled && navigate(option.link)}
            >
              <CardHeader className="pb-4">
                <CardTitle className="flex items-center gap-3">
                  <div className={`p-3 rounded-lg bg-gradient-to-br from-slate-100 to-blue-50 shadow-sm ${option.color}`}>
                    <Icon className="h-6 w-6" />
                  </div>
                  <div>
                    <span className="text-slate-800 font-semibold">{option.title}</span>
                    {option.disabled && (
                      <span className="ml-2 text-xs text-slate-400 font-normal">
                        (Coming Soon)
                      </span>
                    )}
                  </div>
                </CardTitle>
                <CardDescription className="mt-2 text-slate-600 font-medium">
                  {option.description}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button 
                  variant="outline" 
                  className={`w-full transition-all duration-200 ${
                    option.disabled 
                      ? 'opacity-50' 
                      : 'hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 hover:text-slate-900 hover:border-blue-300'
                  }`}
                  disabled={option.disabled}
                  onClick={(e) => {
                    e.stopPropagation()
                    if (!option.disabled) navigate(option.link)
                  }}
                >
                  <span className="font-medium">{option.disabled ? 'Coming Soon' : 'Open Settings'}</span>
                </Button>
              </CardContent>
            </Card>
          )
        })}
      </div>

      {/* Quick Actions */}
      <Card className="bg-gradient-to-br from-white to-slate-50 border-slate-200/60 shadow-sm backdrop-blur-sm">
        <CardHeader className="bg-gradient-to-r from-slate-50/50 to-blue-50/50 rounded-t-lg border-b border-slate-200/60">
          <CardTitle className="text-slate-800 font-semibold">Quick Actions</CardTitle>
          <CardDescription className="text-slate-600 font-medium">
            Common settings and actions you can perform
          </CardDescription>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-4 gap-4 bg-white/50 backdrop-blur-sm">
          <Button 
            variant="outline"
            className="h-auto py-4 flex-col gap-2 hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 hover:text-slate-900 hover:border-blue-300 transition-all duration-200"
            onClick={() => navigate('/admin/company-info')}
          >
            <Database className="h-5 w-5 text-blue-500" />
            <span className="text-sm font-medium">Edit Company Info</span>
          </Button>

          <Button 
            variant="outline"
            className="h-auto py-4 flex-col gap-2 hover:bg-gradient-to-r hover:from-green-50 hover:to-emerald-50 hover:text-slate-900 hover:border-green-300 transition-all duration-200"
            onClick={() => navigate('/admin/notifications')}
          >
            <Bell className="h-5 w-5 text-green-500" />
            <span className="text-sm font-medium">View Notifications</span>
          </Button>

          <Button 
            variant="outline"
            className="h-auto py-4 flex-col gap-2 hover:bg-gradient-to-r hover:from-red-50 hover:to-rose-50 hover:text-slate-900 hover:border-red-300 transition-all duration-200"
            onClick={() => navigate('/admin/legal-documents')}
          >
            <FileText className="h-5 w-5 text-red-500" />
            <span className="text-sm font-medium">Legal Documents</span>
          </Button>

          <Button 
            variant="outline"
            className="h-auto py-4 flex-col gap-2 hover:bg-gradient-to-r hover:from-slate-50 hover:to-gray-50 hover:text-slate-900 hover:border-slate-300 transition-all duration-200"
            onClick={() => navigate('/admin/account')}
          >
            <Shield className="h-5 w-5 text-slate-600" />
            <span className="text-sm font-medium">Account Settings</span>
          </Button>
        </CardContent>
      </Card>
    </div>
  )
}

export default Settings
